import React, { useState } from 'react';
import { mockUsers, mockAgents, Agent } from '../data/mockData';
import PageFooter from '../components/PageFooter';
import './Admin.css';

const Admin: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'users' | 'agents' | 'settings' | 'audit'>('users');
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [newAgent, setNewAgent] = useState<Partial<Agent>>({
    name: '',
    version: '1.0.0',
    category: 'Development',
    tags: [],
    description: '',
    owner: '',
    status: 'active',
    endpoint: '',
    authMethod: 'API Key',
    rateLimit: '100 requests/hour',
    sla: '99.9% uptime',
    inputSchema: {},
    outputSchema: {},
    usageCount: 0,
    documentation: ''
  });
  const [tagInput, setTagInput] = useState('');

  const handleRegisterAgent = () => {
    setShowAgentModal(true);
  };

  const handleSaveAgent = () => {
    console.log('Registering new agent:', newAgent);
    alert(`Agent "${newAgent.name}" registered successfully! (Mock)\n\nIn production, this would:\n1. Validate the agent metadata\n2. Save to database\n3. Test the endpoint connection\n4. Add to agent catalog`);
    setShowAgentModal(false);
    // Reset form
    setNewAgent({
      name: '',
      version: '1.0.0',
      category: 'Development',
      tags: [],
      description: '',
      owner: '',
      status: 'active',
      endpoint: '',
      authMethod: 'API Key',
      rateLimit: '100 requests/hour',
      sla: '99.9% uptime',
      inputSchema: {},
      outputSchema: {},
      usageCount: 0,
      documentation: ''
    });
    setTagInput('');
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !newAgent.tags?.includes(tagInput.trim())) {
      setNewAgent({
        ...newAgent,
        tags: [...(newAgent.tags || []), tagInput.trim()]
      });
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    setNewAgent({
      ...newAgent,
      tags: newAgent.tags?.filter(t => t !== tag) || []
    });
  };

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>Administration</h1>
        <p>Manage users, agents, and system settings</p>
      </div>

      <div className="admin-tabs">
        <button
          className={activeTab === 'users' ? 'tab-btn active' : 'tab-btn'}
          onClick={() => setActiveTab('users')}
        >
          Users
        </button>
        <button
          className={activeTab === 'agents' ? 'tab-btn active' : 'tab-btn'}
          onClick={() => setActiveTab('agents')}
        >
          Agent Registry
        </button>
        <button
          className={activeTab === 'settings' ? 'tab-btn active' : 'tab-btn'}
          onClick={() => setActiveTab('settings')}
        >
          Settings
        </button>
        <button
          className={activeTab === 'audit' ? 'tab-btn active' : 'tab-btn'}
          onClick={() => setActiveTab('audit')}
        >
          Audit Logs
        </button>
      </div>

      <div className="admin-content">
        {activeTab === 'users' && (
          <div className="tab-content">
            <div className="content-header">
              <h2>User Management</h2>
              <button className="btn-primary">Add User</button>
            </div>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Permissions</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {mockUsers.map(user => (
                  <tr key={user.id}>
                    <td>{user.username}</td>
                    <td>{user.email}</td>
                    <td><span className="role-badge">{user.role}</span></td>
                    <td>{user.permissions.length} permissions</td>
                    <td>
                      <button className="btn-small">Edit</button>
                      <button className="btn-small btn-danger">Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'agents' && (
          <div className="tab-content">
            <div className="content-header">
              <h2>Agent Registry</h2>
              <button className="btn-primary" onClick={handleRegisterAgent}>Register New Agent</button>
            </div>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Agent Name</th>
                  <th>Version</th>
                  <th>Category</th>
                  <th>Status</th>
                  <th>Usage</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {mockAgents.map(agent => (
                  <tr key={agent.id}>
                    <td>{agent.name}</td>
                    <td>{agent.version}</td>
                    <td>{agent.category}</td>
                    <td><span className={`status-badge ${agent.status}`}>{agent.status}</span></td>
                    <td>{agent.usageCount}</td>
                    <td>
                      <button className="btn-small">Edit</button>
                      <button className="btn-small">Deprecate</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="tab-content">
            <h2>System Settings</h2>
            <div className="settings-grid">
              <div className="setting-card">
                <h3>Authentication</h3>
                <div className="setting-item">
                  <label>SSO Provider</label>
                  <select>
                    <option>Azure AD</option>
                    <option>Okta</option>
                    <option>Auth0</option>
                  </select>
                </div>
                <div className="setting-item">
                  <label>
                    <input type="checkbox" defaultChecked />
                    Enable Multi-Factor Authentication
                  </label>
                </div>
              </div>

              <div className="setting-card">
                <h3>Workflow Engine</h3>
                <div className="setting-item">
                  <label>Max Concurrent Executions</label>
                  <input type="number" defaultValue="10" />
                </div>
                <div className="setting-item">
                  <label>Default Timeout (seconds)</label>
                  <input type="number" defaultValue="300" />
                </div>
              </div>

              <div className="setting-card">
                <h3>Notifications</h3>
                <div className="setting-item">
                  <label>
                    <input type="checkbox" defaultChecked />
                    Email Notifications
                  </label>
                </div>
                <div className="setting-item">
                  <label>
                    <input type="checkbox" defaultChecked />
                    Slack Integration
                  </label>
                </div>
              </div>

              <div className="setting-card">
                <h3>Security</h3>
                <div className="setting-item">
                  <label>
                    <input type="checkbox" defaultChecked />
                    Enable Audit Logging
                  </label>
                </div>
                <div className="setting-item">
                  <label>
                    <input type="checkbox" defaultChecked />
                    Encrypt Data at Rest
                  </label>
                </div>
              </div>
            </div>
            <button className="btn-primary" style={{ marginTop: '20px' }}>Save Settings</button>
          </div>
        )}

        {activeTab === 'audit' && (
          <div className="tab-content">
            <h2>Audit Logs</h2>
            <div className="audit-filters">
              <input type="date" />
              <select>
                <option>All Actions</option>
                <option>User Login</option>
                <option>Workflow Execution</option>
                <option>Agent Registration</option>
              </select>
              <button className="btn-secondary">Filter</button>
            </div>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>User</th>
                  <th>Action</th>
                  <th>Resource</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>2024-12-29 10:15:30</td>
                  <td>admin</td>
                  <td>Workflow Execution</td>
                  <td>SDLC Complete Pipeline</td>
                  <td><span className="status-success">Success</span></td>
                </tr>
                <tr>
                  <td>2024-12-29 09:45:12</td>
                  <td>developer</td>
                  <td>Agent Registration</td>
                  <td>Code Generator Agent v1.5.0</td>
                  <td><span className="status-success">Success</span></td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Agent Registration Modal */}
      {showAgentModal && (
        <div className="agent-details-modal" onClick={() => setShowAgentModal(false)}>
          <div className="agent-details register-agent-modal" onClick={(e) => e.stopPropagation()}>
            <div className="details-header">
              <h2>Register New Agent</h2>
              <button className="close-btn" onClick={() => setShowAgentModal(false)}>×</button>
            </div>

            <div className="details-content" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              <div className="detail-section">
                <h3>Basic Information</h3>

                <div className="form-group">
                  <label>Agent Name *</label>
                  <input
                    type="text"
                    placeholder="e.g., BRD Generator Agent"
                    value={newAgent.name}
                    onChange={(e) => setNewAgent({ ...newAgent, name: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Version *</label>
                  <input
                    type="text"
                    placeholder="e.g., 1.0.0"
                    value={newAgent.version}
                    onChange={(e) => setNewAgent({ ...newAgent, version: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Category *</label>
                  <select
                    value={newAgent.category}
                    onChange={(e) => setNewAgent({ ...newAgent, category: e.target.value })}
                  >
                    <option value="Requirements">Requirements</option>
                    <option value="Development">Development</option>
                    <option value="Testing">Testing</option>
                    <option value="Documentation">Documentation</option>
                    <option value="Deployment">Deployment</option>
                    <option value="Monitoring">Monitoring</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Owner *</label>
                  <input
                    type="text"
                    placeholder="e.g., SDLC Team"
                    value={newAgent.owner}
                    onChange={(e) => setNewAgent({ ...newAgent, owner: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Description *</label>
                  <textarea
                    placeholder="Describe what this agent does..."
                    value={newAgent.description}
                    onChange={(e) => setNewAgent({ ...newAgent, description: e.target.value })}
                    rows={3}
                  />
                </div>

                <div className="form-group">
                  <label>Tags</label>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                    <input
                      type="text"
                      placeholder="Add a tag..."
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                    />
                    <button className="btn-secondary" onClick={handleAddTag}>Add</button>
                  </div>
                  <div className="agent-tags">
                    {newAgent.tags?.map(tag => (
                      <span key={tag} className="tag" style={{ cursor: 'pointer' }} onClick={() => handleRemoveTag(tag)}>
                        {tag} ×
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h3>Technical Details</h3>

                <div className="form-group">
                  <label>Endpoint URL *</label>
                  <input
                    type="text"
                    placeholder="e.g., https://api.example.com/agents/my-agent"
                    value={newAgent.endpoint}
                    onChange={(e) => setNewAgent({ ...newAgent, endpoint: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Authentication Method *</label>
                  <select
                    value={newAgent.authMethod}
                    onChange={(e) => setNewAgent({ ...newAgent, authMethod: e.target.value })}
                  >
                    <option value="API Key">API Key</option>
                    <option value="OAuth 2.0">OAuth 2.0</option>
                    <option value="Bearer Token">Bearer Token</option>
                    <option value="Basic Auth">Basic Auth</option>
                    <option value="None">None</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Rate Limit</label>
                  <input
                    type="text"
                    placeholder="e.g., 100 requests/hour"
                    value={newAgent.rateLimit}
                    onChange={(e) => setNewAgent({ ...newAgent, rateLimit: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>SLA</label>
                  <input
                    type="text"
                    placeholder="e.g., 99.9% uptime"
                    value={newAgent.sla}
                    onChange={(e) => setNewAgent({ ...newAgent, sla: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Documentation URL</label>
                  <input
                    type="text"
                    placeholder="e.g., https://docs.example.com/agents/my-agent"
                    value={newAgent.documentation}
                    onChange={(e) => setNewAgent({ ...newAgent, documentation: e.target.value })}
                  />
                </div>
              </div>

              <div className="detail-section">
                <h3>Schema (JSON Format)</h3>

                <div className="form-group">
                  <label>Input Schema *</label>
                  <textarea
                    placeholder='{"param1": "string", "param2": "number"}'
                    value={typeof newAgent.inputSchema === 'string' ? newAgent.inputSchema : JSON.stringify(newAgent.inputSchema, null, 2)}
                    onChange={(e) => {
                      try {
                        setNewAgent({ ...newAgent, inputSchema: JSON.parse(e.target.value) });
                      } catch {
                        setNewAgent({ ...newAgent, inputSchema: e.target.value as any });
                      }
                    }}
                    rows={4}
                  />
                </div>

                <div className="form-group">
                  <label>Output Schema *</label>
                  <textarea
                    placeholder='{"result": "string", "status": "string"}'
                    value={typeof newAgent.outputSchema === 'string' ? newAgent.outputSchema : JSON.stringify(newAgent.outputSchema, null, 2)}
                    onChange={(e) => {
                      try {
                        setNewAgent({ ...newAgent, outputSchema: JSON.parse(e.target.value) });
                      } catch {
                        setNewAgent({ ...newAgent, outputSchema: e.target.value as any });
                      }
                    }}
                    rows={4}
                  />
                </div>
              </div>
            </div>

            <div className="details-footer">
              <button className="btn-secondary" onClick={() => setShowAgentModal(false)}>Cancel</button>
              <button
                className="btn-primary"
                onClick={handleSaveAgent}
                disabled={!newAgent.name || !newAgent.endpoint || !newAgent.description}
              >
                Register Agent
              </button>
            </div>
          </div>
        </div>
      )}

      <PageFooter />
    </div>
  );
};

export default Admin;

